import { gql, useQuery } from '@apollo/client';
import { Button, Link } from '@apollo/orbit';
import React, { useEffect, useState } from 'react';
import {
  Link as ReactRouterLink,
  useHistory,
  useLocation,
  useRouteMatch,
} from 'react-router-dom';

import {
  graphRefToString,
  parseGraphRef,
} from 'src/app/graph/hooks/useGraphRef';
import {
  explorerRouteConfig,
  subgraphsRouteConfig,
} from 'src/app/graph/routes';
import { useShowToast } from 'src/components/toast/Toast';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { catchAllRouteConfig } from 'src/lib/routeConfig/catchAllRoute';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { useCurrentAccountId } from './useCurrentAccountId';
import { getRouterIsInitiating } from './useIsCloudEndpointInitiating';
import { useQueryParam } from './useQueryParam';
import { useRouteParams } from './useRouteParams';
import { useShouldSeeSupergraphAccountView } from './useShouldSeeSupergraphAccountView';

export const useShowCloudRouterEndpointStatusToast = () => {
  const { shouldSeeSupergraphAccountView } =
    useShouldSeeSupergraphAccountView();
  const [previousCloudRouterState, setPreviousCloudRouterState] = useState<
    Record<
      string,
      {
        status: GraphQLTypes.RouterStatus | undefined;
        latestLaunch: GraphQLTypes.UseShowCloudRouterEndpointStatusToastQuery_account_services_variants_latestLaunch | null;
      }
    >
  >();
  const [someCloudRouterIsBeingCreated, setSomeCloudRouterIsBeingCreated] =
    useState(true);
  const [currentAccountId] = useCurrentAccountId();

  const skip =
    !shouldSeeSupergraphAccountView ||
    !someCloudRouterIsBeingCreated ||
    !currentAccountId;

  const [hasBeenPollingFor5Min, setHasBeenPollingFor5Min] = useState(false);
  useEffect(() => {
    if (skip) {
      setHasBeenPollingFor5Min(false);
    } else {
      const timeout = setTimeout(() => {
        setHasBeenPollingFor5Min(true);
      }, 300_000); // 5 min in ms
      return () => clearTimeout(timeout);
    }
  }, [skip]);

  const { data, loading } = useQuery(
    gql<
      GraphQLTypes.UseShowCloudRouterEndpointStatusToastQuery,
      GraphQLTypes.UseShowCloudRouterEndpointStatusToastQueryVariables
    >`
      query UseShowCloudRouterEndpointStatusToastQuery($accountId: ID!) {
        account(id: $accountId) {
          id
          services {
            id
            graphType
            variants {
              id
              name
              router {
                id
                routerUrl # include this in the fetch so the url is populated in the cache as soon as the status changes
                status
              }
              subgraphs {
                name
              }
              latestLaunch {
                id
                status
                build {
                  id
                  result {
                    ... on BuildFailure {
                      errorMessages {
                        message
                      }
                    }
                  }
                }
              }
              graph {
                id
              }
            }
          }
        }
      }
    `,
    {
      skip: skip || hasBeenPollingFor5Min,
      pollInterval: 3000,
      variables: {
        accountId: currentAccountId as string,
      },
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
    },
  );

  useEffect(() => {
    if (previousCloudRouterState) {
      setSomeCloudRouterIsBeingCreated(
        Object.values(previousCloudRouterState).some(
          ({ status, latestLaunch }) =>
            getRouterIsInitiating({
              graphType: GraphQLTypes.GraphType.CLOUD_SUPERGRAPH,
              latestLaunch,
              routerStatus: status,
            }),
        ),
      );
    }
  }, [previousCloudRouterState]);

  const { showToasts } = useShowToast();
  const location = useLocation();
  const history = useHistory();

  const isOnExplorerRoute =
    useRouteMatch(explorerRouteConfig.definition) !== null;
  const { graphId: graphIdFromUrl, graphVariant: variantFromUrl } =
    useRouteParams(explorerRouteConfig, catchAllRouteConfig);

  const overlay = useQueryParam('overlay');
  const hasInitiatingEndpointModalOpen =
    overlay === Config.modals.initiatingCloudEndpoint;

  useEffect(() => {
    if (data && !loading) {
      const cloudRouterStatusByVariantId = data.account
        ? Object.fromEntries(
            data.account.services
              .filter(
                (graph) =>
                  graph.graphType === GraphQLTypes.GraphType.CLOUD_SUPERGRAPH,
              )
              .flatMap((graph) => graph.variants)
              .map((variant) => [
                graphRefToString({
                  graphId: variant.graph.id,
                  graphVariant: variant.name,
                }),
                {
                  status: variant.router?.status,
                  latestLaunch: variant.latestLaunch,
                },
              ]),
          )
        : undefined;

      if (cloudRouterStatusByVariantId) {
        setPreviousCloudRouterState((previousCloudRouterStateByIdLocal) => {
          Object.entries(cloudRouterStatusByVariantId).forEach(
            ([id, currentRouterState]) => {
              const graphRef = parseGraphRef(id);
              const thisVariant = data.account?.services
                .find((service) => service.id === graphRef?.graphId)
                ?.variants.find(
                  (variant) => variant.name === graphRef?.graphVariant,
                );
              const buildErrors =
                thisVariant?.latestLaunch?.build?.result?.__typename ===
                'BuildFailure'
                  ? thisVariant.latestLaunch.build.result.errorMessages
                  : undefined;

              const previousCloudRouterStateLocal =
                previousCloudRouterStateByIdLocal?.[id];
              if (previousCloudRouterStateLocal) {
                const {
                  status: currentRouterStatus,
                  latestLaunch: currentLatestLaunch,
                } = currentRouterState;
                const {
                  status: previousRouterStatus,
                  latestLaunch: previousLatestLaunch,
                } = previousCloudRouterStateLocal;
                if (
                  getRouterIsInitiating({
                    graphType: GraphQLTypes.GraphType.CLOUD_SUPERGRAPH,
                    latestLaunch: previousLatestLaunch,
                    routerStatus: previousRouterStatus,
                  }) &&
                  !getRouterIsInitiating({
                    graphType: GraphQLTypes.GraphType.CLOUD_SUPERGRAPH,
                    latestLaunch: currentLatestLaunch,
                    routerStatus: currentRouterStatus,
                  })
                ) {
                  // if the modal is open, we will show success in the modal!
                  if (!hasInitiatingEndpointModalOpen) {
                    if (
                      isOnExplorerRoute &&
                      graphIdFromUrl === graphRef?.graphId &&
                      variantFromUrl === graphRef?.graphVariant
                    ) {
                      history.push({
                        ...history.location,
                        state: {
                          ...(typeof history.location.state === 'object'
                            ? history.location.state
                            : {}),
                          showSupergraphEndpointSuccessNudge: 'true',
                        },
                      });
                    } else if (
                      currentRouterStatus === GraphQLTypes.RouterStatus.RUNNING
                    ) {
                      showToasts({
                        heading: `${id} is ready to query 🎉`,
                        level: 'success',
                        theme: 'dark',
                        message: (
                          <>
                            <div>
                              Head over to Explorer to start running queries
                              against your supergraph endpoint!
                            </div>
                            <Button
                              size="sm"
                              variant="brandDark"
                              className="mt-4"
                              to={explorerRouteConfig.location({
                                graphId: graphRef?.graphId as string,
                                graphVariant: graphRef?.graphVariant as string,
                              })}
                              as={ReactRouterLink}
                            >
                              Query your supergraph
                            </Button>
                          </>
                        ),
                      });
                    } else if (isMinLengthArray(1, buildErrors)) {
                      /**
                       * The launch will be populated before the router starts initializing in the backend
                       */
                      showToasts({
                        heading: `Oh no! The build for ${id} failed 😟`,
                        level: 'error',
                        theme: 'dark',
                        message: (
                          <>
                            Fix the build errors and
                            <Link
                              to={
                                thisVariant?.subgraphs?.[0]?.name
                                  ? setOverlayVisibleLocation({
                                      location: subgraphsRouteConfig.location({
                                        graphId: graphRef?.graphId as string,
                                        graphVariant:
                                          graphRef?.graphVariant as string,
                                        reuploadSubgraphModalState: {
                                          subgraphName:
                                            thisVariant?.subgraphs?.[0]?.name,
                                        },
                                      }),
                                      overlayName:
                                        Config.modals.reuploadSubgraph,
                                    })
                                  : subgraphsRouteConfig.location({
                                      graphId: graphRef?.graphId as string,
                                      graphVariant:
                                        graphRef?.graphVariant as string,
                                    })
                              }
                            >
                              {' '}
                              reupload this subgraph
                            </Link>
                            , or{' '}
                            <Link
                              target="_blank"
                              rel="noopener noreferrer"
                              to={setOverlayVisibleLocation({
                                location: subgraphsRouteConfig.location({
                                  graphId: graphRef?.graphId as string,
                                  graphVariant:
                                    graphRef?.graphVariant as string,
                                }),
                                overlayName: Config.modals.addSubgraph,
                              })}
                            >
                              upload a second subgraph
                            </Link>{' '}
                            to trigger composition.
                          </>
                        ),
                      });
                    } else {
                      showToasts({
                        heading: `Oh no! Looks like something went wrong with ${id} 😟`,
                        level: 'error',
                        theme: 'dark',
                        message: (
                          <>
                            <div>
                              Try recreating your supergraph or submit a support
                              ticket and we’ll take a look at this for you as
                              soon as possible!
                            </div>
                            <Button
                              size="sm"
                              variant="brandDark"
                              className="mt-4"
                              as={ReactRouterLink}
                              to={setOverlayVisibleLocation({
                                location,
                                overlayName: Config.modals.supportRequest,
                              })}
                            >
                              Contact support
                            </Button>
                          </>
                        ),
                      });
                    }
                  }
                }
              }
            },
          );
          return cloudRouterStatusByVariantId;
        });
      }
    }
  }, [
    data,
    graphIdFromUrl,
    hasInitiatingEndpointModalOpen,
    history,
    isOnExplorerRoute,
    loading,
    location,
    location.search,
    showToasts,
    variantFromUrl,
  ]);

  return {
    triggerRefetch: () => {
      setSomeCloudRouterIsBeingCreated(true);
      // reset polling timeout when refetching
      setHasBeenPollingFor5Min(false);
    },
  };
};
